import { useEffect, useState } from "react";
import { type UserVar, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

export default function Variables({ syncKey }: Props) {
  const [vars, setVars] = useState<UserVar[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("null");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const res = await api.vars.list(syncKey);
      setVars(res.vars);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function startEdit(v: UserVar) {
    setEditingKey(v.key);
    try {
      setEditValue(JSON.stringify(JSON.parse(v.value), null, 2));
    } catch {
      setEditValue(v.value);
    }
    setError("");
  }

  async function saveEdit(key: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(editValue);
    } catch {
      setError("value must be valid JSON");
      return;
    }
    setError("");
    await api.vars.set(syncKey, key, parsed).catch(() => {});
    setEditingKey(null);
    load();
  }

  async function deleteVar(key: string) {
    await api.vars.delete(syncKey, key).catch(() => {});
    load();
  }

  async function createVar() {
    if (!newKey.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(newValue);
    } catch {
      setError("value must be valid JSON");
      return;
    }
    setError("");
    setCreating(true);
    try {
      await api.vars.set(syncKey, newKey.trim(), parsed);
      setNewKey("");
      setNewValue("null");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setCreating(false);
    }
  }

  function fmtValue(raw: string) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") return `"${parsed.slice(0, 60)}${parsed.length > 60 ? "…" : ""}"`;
      return JSON.stringify(parsed).slice(0, 80);
    } catch {
      return raw.slice(0, 80);
    }
  }

  function fmtDate(ts: number) {
    return new Date(ts * 1000).toLocaleString();
  }

  return (
    <div className="page">
      <h1>Variables</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>
        Persistent key-value store. Scripts and plugins can read these values at runtime via{" "}
        <code>Config.get(key)</code>. Values are JSON — strings, numbers, objects, arrays, or null.
      </p>

      {/* Create */}
      <div className="settings-section" style={{ marginBottom: 24 }}>
        <h2>New variable</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Key</label>
            <input
              placeholder="MY_API_KEY"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
              onKeyDown={(e) => e.key === "Enter" && createVar()}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
              Value (JSON)
            </label>
            <input
              placeholder='"my-value"'
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
              onKeyDown={(e) => e.key === "Enter" && createVar()}
            />
          </div>
        </div>
        {error && <p style={{ color: "#f87171", fontSize: 12, marginTop: 6 }}>{error}</p>}
        <button
          type="button"
          onClick={createVar}
          disabled={creating || !newKey.trim()}
          style={{ marginTop: 10 }}
        >
          {creating ? "Saving…" : "Add Variable"}
        </button>
      </div>

      {/* List */}
      {loading && vars.length === 0 ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : vars.length === 0 ? (
        <p className="empty">No variables yet.</p>
      ) : (
        vars.map((v) => (
          <div key={v.key} className="settings-section" style={{ marginBottom: 8, padding: "10px 14px" }}>
            {editingKey === v.key ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <code style={{ fontFamily: "monospace", fontSize: 13, color: "#e2e8f0", flex: 1 }}>
                    {v.key}
                  </code>
                  <button
                    type="button"
                    onClick={() => saveEdit(v.key)}
                    style={{ fontSize: 11, padding: "2px 10px" }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingKey(null); setError(""); }}
                    style={{ fontSize: 11, padding: "2px 8px" }}
                  >
                    Cancel
                  </button>
                </div>
                <textarea
                  rows={4}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  style={{ width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
                  spellCheck={false}
                />
                {error && <p style={{ color: "#f87171", fontSize: 12, marginTop: 4 }}>{error}</p>}
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <code style={{ fontFamily: "monospace", fontSize: 13, color: "#e2e8f0", flex: 1 }}>
                  {v.key}
                </code>
                <code style={{ fontFamily: "monospace", fontSize: 11, color: "#94a3b8", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {fmtValue(v.value)}
                </code>
                <span style={{ fontSize: 11, color: "#555" }}>{fmtDate(v.updatedAt)}</span>
                <button
                  type="button"
                  onClick={() => startEdit(v)}
                  style={{ fontSize: 11, padding: "2px 8px" }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => deleteVar(v.key)}
                  className="clip-icon-btn"
                  title="Delete"
                >
                  🗑
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
